import React, { useState, useMemo } from 'react';
import { Building2, ChevronDown, Check, Loader2, Plus, Copy, CopyPlus, Layers } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CreateTenantDialog } from '@/components/settings/CreateTenantDialog';
import { getDefaultTenant } from '@/services/domainService';
import { buildTenantHierarchy, type TenantHierarchyRow } from '@/lib/tenantHierarchy';
import { canManageTenants, canSeeTenantPicker } from '@/lib/tenantPicker';
import {
  isMergedScope,
  mergedAccountCount,
  offersMergedScope,
  type MergedScopeStatus,
} from '@/lib/mergedScope';

// The global/platform tenant is not offered as a clone source (TEN-1.4).
const GLOBAL_TENANT_ID = getDefaultTenant().tenantId;

/** SUB-4.1 · FR31. The one label for merged scope, so the two picker variants and
 *  the trigger button cannot drift apart on what it is called. */
const ALL_ACCOUNTS_LABEL = 'All Accounts';

// The tenant shape the picker receives. Taken from the context rather than exported
// from it, so the context's Tenant interface stays private.
type PickerTenant = ReturnType<typeof useTenant>['userTenants'][number];

interface TenantDropdownProps {
  collapsed?: boolean;
}

/**
 * SUB-2.10 · FR17. The name line for one account, carrying its place in the
 * hierarchy: a parent says how many sub-accounts it has, a sub-account is marked
 * and indented by its container, a standalone account renders exactly as before.
 *
 * Shared by both picker variants so the collapsed and expanded lists cannot drift.
 */
function TenantRowLabel({
  row,
  onCopyId,
}: {
  row: TenantHierarchyRow<PickerTenant>;
  onCopyId: (tenantId: string) => void;
}) {
  const { tenant, kind, subAccountCount } = row;

  return (
    <>
      {/* The sidebar is ~224px wide, so nothing may share this line with the name.
          A marker beside it wins the space and truncates the account name to a
          single letter, which is worse than showing no marker at all. */}
      <div className="flex items-center gap-1 min-w-0">
        {kind === 'sub' && (
          <span className="text-gray-400 flex-shrink-0 select-none" aria-hidden="true">
            &#9492;
          </span>
        )}
        <p className="font-medium text-gray-900 truncate">{tenant.company_name}</p>
      </div>
      <div className="flex items-center gap-1 group min-w-0">
        {/* The hierarchy marker rides the ID line, where losing characters costs
            nothing: the id is already truncated and is a copy target, not a label. */}
        {kind !== 'standalone' && (
          <span className="text-xs text-gray-500 flex-shrink-0">
            {kind === 'parent'
              ? `${subAccountCount} ${subAccountCount === 1 ? 'sub-account' : 'sub-accounts'} ·`
              : 'Sub-account ·'}
          </span>
        )}
        <p className="text-xs text-gray-500 truncate">ID: {tenant.tenant_id}</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCopyId(tenant.tenant_id);
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 rounded"
          title="Copy tenant ID"
        >
          <Copy className="h-3 w-3 text-gray-600" />
        </button>
      </div>
    </>
  );
}

/**
 * SUB-4.1 · FR31, FR32, FR71, FR33. The merged scope entry.
 *
 * ⚠️ IT RENDERS ABOVE THE ACCOUNTS, NOT AMONG THEM. The AC says "above the
 * individual accounts", and the reason is not ordering: All Accounts is not an
 * account, and a row sitting in the same list, indented the same way, invites the
 * reading that it is a thirteenth client. The separator below it is the boundary
 * between "a scope" and "an account".
 *
 * ⚠️ THE SECOND LINE IS FR33 AND IT IS NOT CHROME. Merged scope covers Projects,
 * Tasks and the pipeline navigator; every other surface, Settings included, still
 * resolves to ONE account. Naming that account here is what keeps "which account
 * am I in" answerable while a merged list is on screen, and it is the only place
 * the rule is stated to the user. Same standing as SUB-4.3's section labels.
 */
function AllAccountsRow({
  status,
  singleAccountName,
  active,
  disabled,
  onSelect,
}: {
  status: MergedScopeStatus;
  singleAccountName: string | null;
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  // Only meaningful once merged: before that the resolver has handed back one
  // account and a count would be a promise rather than a measurement.
  const count = mergedAccountCount(status);

  return (
    <div className="border-b">
      <div className="flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50">
        <button
          onClick={onSelect}
          disabled={disabled}
          className="flex-1 min-w-0 text-left disabled:opacity-50"
        >
          <div className="flex items-center gap-1 min-w-0">
            <Layers className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" aria-hidden="true" />
            <p className="font-medium text-gray-900 truncate">{ALL_ACCOUNTS_LABEL}</p>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {active && count > 0
              ? `${count} accounts · Settings and other pages stay in ${singleAccountName ?? 'one account'}`
              : 'Your account and all of its sub-accounts'}
          </p>
        </button>
        {active ? (
          <div className="flex items-center space-x-2 ml-2">
            <Check className="h-4 w-4 text-green-600" />
            <Badge variant="default" className="text-xs">Active</Badge>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// A sub-account is indented under its parent. Nothing else about the row changes.
const rowClassName = (depth: 0 | 1) =>
  cn(
    'flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50',
    depth === 1 && 'pl-9',
  );

export function TenantDropdown({ collapsed = false }: TenantDropdownProps) {
  const {
    currentTenant,
    userTenants,
    isAgencyAdmin,
    // SUB-2.90 · F-82. NOT `hasPermission('system.override')`. See the field's doc.
    hasSystemOverride,
    switchTenant,
    mergedScope,
    enterMergedScope,
    isLoading: tenantLoading,
  } = useTenant();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // When set, the create dialog opens in "clone" mode pre-set to this source tenant.
  const [cloneSourceId, setCloneSourceId] = useState<string | null>(null);

  // SUB-2.10 · FR17. Same accounts, same order, grouped so each parent is followed
  // by its sub-accounts. On a platform with no hierarchy this is the identity
  // transform, which is what keeps FR16/NFR1 true.
  const hierarchy = useMemo(
    () => buildTenantHierarchy(userTenants ?? []),
    [userTenants],
  );

  const openCreateBlank = () => {
    setCloneSourceId(null);
    setIsOpen(false);
    setShowCreateDialog(true);
  };

  const openClone = (tenantId: string) => {
    setCloneSourceId(tenantId);
    setIsOpen(false);
    setShowCreateDialog(true);
  };

  // SUB-4.1 · FR31, FR71. Whether merged scope is on offer, and whether this
  // session is in it. Both are the server's answer, asked in TenantContext.
  const inMergedScope = isMergedScope(mergedScope);
  const showAllAccounts = offersMergedScope(mergedScope);

  // FR33. The account every surface outside Projects, Tasks and the pipeline
  // navigator resolves to. That is the anchor, and while merged it is also what
  // `currentTenant` holds, so this is a lookup and not a guess.
  const singleAccountName =
    userTenants.find(t => t.tenant_id === mergedScope.singleAccountTenantId)?.company_name
    ?? currentTenant?.company_name
    ?? null;

  const handleEnterMergedScope = async () => {
    if (isSwitching || inMergedScope) {
      setIsOpen(false);
      return;
    }

    try {
      setIsSwitching(true);
      toast({
        title: `Switching to ${ALL_ACCOUNTS_LABEL}`,
        description: 'Loading your account and all of its sub-accounts...',
      });

      await enterMergedScope();

      // Same reload as a tenant switch, and for the same reason: entering merged
      // scope re-anchors the session at the parent account, so every cached
      // resolution in the tree is now describing a different scope.
      window.location.replace('/dashboard');
    } catch (error) {
      console.error('Error entering merged scope:', error);
      toast({
        title: 'Error',
        description: `Failed to switch to ${ALL_ACCOUNTS_LABEL}. Please try again.`,
        variant: 'destructive',
      });
      setIsSwitching(false);
    }
  };

  const handleTenantSwitch = async (tenantId: string) => {
    // ⚠️ `&& !inMergedScope` IS LORE-BEARING. In merged scope `currentTenant` is
    // the ANCHOR, which is the parent account, so without this term clicking the
    // parent is a no-op and an Agency Admin in All Accounts has no way to select
    // the one account the row names. Choosing an account is how merged scope is
    // left (set_superadmin_active_tenant writes scope_mode = 'single'), so this
    // early return would have swallowed the only exit.
    if ((tenantId === currentTenant?.tenant_id && !inMergedScope) || isSwitching) {
      setIsOpen(false);
      return;
    }

    try {
      setIsSwitching(true);
      setSwitchingTo(tenantId);
      const tenant = userTenants.find(t => t.tenant_id === tenantId);

      // Show switching toast
      toast({
        title: "Switching Tenant",
        description: `Switching to ${tenant?.company_name}...`,
      });

      // Wait for tenant switch to complete before redirecting
      await switchTenant(tenantId);

      // Redirect after successful switch
      window.location.replace('/dashboard');

    } catch (error) {
      console.error('Error switching tenant:', error);
      toast({
        title: "Error",
        description: "Failed to switch tenant. Please try again.",
        variant: "destructive",
      });
      setSwitchingTo(null);
      setIsSwitching(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  // SUB-2.7 · FR12. Superadmins as before, plus Agency Admins, who are the only
  // other users with more than one account to be in. The list itself is not
  // filtered here: TenantContext resolves it from the grant table and excludes
  // archived accounts in the database (FR67), so what an Agency Admin sees is
  // their parent, its sub-accounts, and any unrelated account they hold a role in
  // (FR65). Everyone else still sees nothing, which is what keeps FR19 true.
  // ⚠️ SUB-2.90 · F-82. `hasSystemOverride` comes from TenantContext, NOT from
  // `hasPermission('system.override')`. It must be the same answer that decided
  // `isAgencyAdmin`: the superadmin branch of `fetchUserTenants` clears that flag
  // because this one covers for it, and the two determinations disagree on a user
  // holding Superadmin through `user_roles` while `users.role_id` points elsewhere.
  // Reading the permission here made both terms false and hid the picker from a
  // superadmin who was also an Agency Admin.
  const viewer = {
    hasSystemOverride,
    isAgencyAdmin,
    tenantCount: userTenants?.length ?? 0,
  };

  if (!canSeeTenantPicker(viewer)) {
    return null;
  }

  // Creating and cloning accounts stay superadmin-only. The database refuses them
  // to anyone else; this stops them being offered.
  const showTenantManagement = canManageTenants(viewer);

  if (collapsed) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-full p-2 rounded-md text-sm font-medium transition-all duration-200 ease-in-out hover:bg-gray-600 hover:text-white"
          style={{ color: 'var(--sidebar-text-color, #ffffff)' }}
          title={
            inMergedScope
              ? `${ALL_ACCOUNTS_LABEL} — Settings and other pages stay in ${singleAccountName ?? 'one account'}`
              : currentTenant?.company_name || 'Select Tenant'
          }
        >
          {/* FR31. The icon changes with the scope, so a 40px-wide sidebar still
              says which of the two the session is in. */}
          {inMergedScope ? <Layers className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
        </button>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <div className="absolute left-full top-0 ml-2 w-64 bg-white rounded-lg shadow-lg border z-50">
              <div className="p-3 border-b">
                <h3 className="font-medium text-gray-900">Switch Tenant</h3>
              </div>
              {/* Outside the scroll container on purpose: a scope is not one of
                  the accounts, and it must not scroll out of view behind them. */}
              {showAllAccounts && (
                <AllAccountsRow
                  status={mergedScope}
                  singleAccountName={singleAccountName}
                  active={inMergedScope}
                  disabled={isSwitching}
                  onSelect={handleEnterMergedScope}
                />
              )}
              <div className="py-2 max-h-80 overflow-y-auto">
                {hierarchy.map((row) => (
                  <div key={row.tenant.tenant_id} className={rowClassName(row.depth)}>
                    <button
                      onClick={() => handleTenantSwitch(row.tenant.tenant_id)}
                      disabled={switchingTo === row.tenant.tenant_id || isSwitching}
                      className="flex-1 min-w-0 text-left disabled:opacity-50"
                    >
                      <TenantRowLabel
                        row={row}
                        onCopyId={(id) => copyToClipboard(id, 'Tenant ID')}
                      />
                    </button>
                    <div className="flex items-center space-x-2 ml-2">
                      {showTenantManagement && row.tenant.tenant_id !== GLOBAL_TENANT_ID && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openClone(row.tenant.tenant_id);
                          }}
                          disabled={isSwitching}
                          className="p-1 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-50"
                          title={`Clone ${row.tenant.company_name}`}
                        >
                          <CopyPlus className="h-4 w-4" />
                        </button>
                      )}
                      {switchingTo === row.tenant.tenant_id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : !inMergedScope && currentTenant?.tenant_id === row.tenant.tenant_id ? (
                        <>
                          <Check className="h-4 w-4 text-green-600" />
                          <Badge variant="default" className="text-xs">Active</Badge>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              {showTenantManagement && (
                <div className="border-t">
                  <button
                    onClick={openCreateBlank}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Tenant
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        <CreateTenantDialog
          open={showCreateDialog}
          onOpenChange={(o) => {
            setShowCreateDialog(o);
            if (!o) setCloneSourceId(null);
          }}
          cloneFromTenantId={cloneSourceId}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ease-in-out hover:bg-gray-600 hover:text-white"
        style={{ color: 'var(--sidebar-text-color, #ffffff)' }}
      >
        <div className="flex items-center min-w-0 flex-1">
          {inMergedScope ? (
            <Layers className="h-5 w-5 mr-3 flex-shrink-0" />
          ) : (
            <Building2 className="h-5 w-5 mr-3 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            {tenantLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading...</span>
              </div>
            ) : inMergedScope ? (
              /* FR33 again, and this is the copy that carries it while a merged
                 list is on screen. The name goes on its OWN line: the sidebar is
                 ~224px and anything sharing the scope's line truncates the one
                 word that says which scope this is. */
              <div className="min-w-0">
                <div className="truncate">
                  <span className="font-medium">{ALL_ACCOUNTS_LABEL}</span>
                </div>
                <div className="truncate text-xs opacity-75">
                  Settings stay in {singleAccountName ?? 'one account'}
                </div>
              </div>
            ) : currentTenant ? (
              <div className="truncate">
                <span className="font-medium">{currentTenant.company_name}</span>
              </div>
            ) : (
              <span className="text-gray-400">No tenant</span>
            )}
          </div>
        </div>
        <ChevronDown 
          className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-180")} 
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50">
            <div className="p-3 border-b">
              <h3 className="font-medium text-gray-900">Switch Tenant</h3>
            </div>
            {/* See the collapsed variant: above the list and outside its scroll. */}
            {showAllAccounts && (
              <AllAccountsRow
                status={mergedScope}
                singleAccountName={singleAccountName}
                active={inMergedScope}
                disabled={isSwitching}
                onSelect={handleEnterMergedScope}
              />
            )}
            <div className="py-2 max-h-64 overflow-y-auto">
              {hierarchy.map((row) => (
                <div key={row.tenant.tenant_id} className={rowClassName(row.depth)}>
                  <button
                    onClick={() => handleTenantSwitch(row.tenant.tenant_id)}
                    disabled={switchingTo === row.tenant.tenant_id || isSwitching}
                    className="flex-1 min-w-0 text-left disabled:opacity-50"
                  >
                    <TenantRowLabel
                      row={row}
                      onCopyId={(id) => copyToClipboard(id, 'Tenant ID')}
                    />
                  </button>
                  <div className="flex items-center space-x-2 ml-2">
                    {/* ⚠️ NOT ACTIVE WHILE MERGED. `currentTenant` is the anchor
                        in merged scope, so without the first term the parent
                        account and the All Accounts row would both read Active
                        and the picker would be claiming two current scopes. */}
                    {switchingTo === row.tenant.tenant_id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : !inMergedScope && currentTenant?.tenant_id === row.tenant.tenant_id ? (
                      <>
                        <Check className="h-4 w-4 text-green-600" />
                        <Badge variant="default" className="text-xs">Active</Badge>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {showTenantManagement && (
              <div className="border-t">
                <button
                  onClick={openCreateBlank}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Tenant
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <CreateTenantDialog
        open={showCreateDialog}
        onOpenChange={(o) => {
          setShowCreateDialog(o);
          if (!o) setCloneSourceId(null);
        }}
        cloneFromTenantId={cloneSourceId}
      />
    </div>
  );
}
